import axiosInstance from './axiosInstance';

export interface Notification {
  id: string;
  type: 'order' | 'delivery' | 'system' | 'alert';
  title: string;
  message: string;
  read: boolean;
  timestamp: string;
  createdAt?: string;
  orderId?: string;
  driverId?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  data?: any;
  sender?: {
    name: string;
    role: string;
    imageURL?: string;
  };
}

class NotificationService {
  private baseEndpoint = '/notifications';

  /**
   * Get all notifications for the current user
   */
  async getNotifications(): Promise<{ notifications: Notification[]; notificationCount: number }> {
    try {
      const response = await axiosInstance.get(this.baseEndpoint);
      return {
        notifications: response.data.notifications || [],
        notificationCount: response.data.notificationCount || 0
      };
    } catch (error: any) {
      console.error('❌ Error fetching notifications:', error);
      
      if (error?.response?.status === 404) {
        console.warn('⚠️ NOTIFICATION API NOT IMPLEMENTED');
        console.warn('⚠️ Backend needs to implement: GET /api/notifications');
        console.warn('⚠️ See NOTIFICATION_BACKEND_REQUIREMENTS.md for implementation');
        console.warn('⚠️ Real-time Socket.IO notifications will still work!');
      }
      
      return { notifications: [], notificationCount: 0 };
    }
  }

  /**
   * Mark a single notification as read
   */
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/${notificationId}/read`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<boolean> {
    try {
      const response = await axiosInstance.patch(`${this.baseEndpoint}/read-all`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return false;
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<boolean> {
    try {
      const response = await axiosInstance.delete(`${this.baseEndpoint}/${notificationId}`);
      return response.data?.success || false;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();

// Export convenience functions
export const getNotifications = () => notificationService.getNotifications();
export const markNotificationAsRead = (id: string) => notificationService.markAsRead(id);
export const markAllNotificationsAsRead = () => notificationService.markAllAsRead();
export const deleteNotification = (id: string) => notificationService.deleteNotification(id);
